import { useMemo, useState } from 'react';
import { CoachCard } from '../../design-system/concept/coach-card';
import { Eyebrow } from '../../design-system/concept/eyebrow';
import { ScanScrubber } from '../../design-system/scan/scan-scrubber';
import { TwinView } from '../coach/twin-view';
import { downloadAvatar } from '../../lib/avatarExport';
import type { MeasurementDelta } from '../../lib/dashboard/measurementDelta';
import { useFeature } from '../../lib/features';
import type { KeyLabel } from '../../lib/domainConfig';
import type { MeasurementResource, ObjectModel } from '../../lib/types';

const unitFor = (key: string) =>
  key === 'body_fat' || key === 'muscle_percent'
    ? '%'
    : key === 'weight' || key === 'muscle_mass'
      ? 'kg'
      : 'cm';

/**
 * A measurement off the mesh.
 *
 * The original was a fourteen-case switch that named every field by hand. The
 * fields are already keyed by exactly the strings the config uses, so an index
 * does the same job and cannot fall out of step when a measurement is added.
 */
const valueOf = (measurements: MeasurementResource, key: string): number => {
  const raw = (measurements as unknown as Record<string, number | undefined>)[key];
  return typeof raw === 'number' ? Math.round(raw) : 0;
};

/**
 * The Avatar section: scrub through the scans, with the projected twin beside
 * the scrubbed one and every tape reading carrying its delta.
 *
 * Rewritten rather than restyled. The previous version was an IonGrid of
 * IonItems around a canvas column that was rendered and then hidden with
 * `className={goalModel ? 'hidden' : ''}` — a whole second WebGL context
 * mounted to be invisible whenever a target was set.
 */
export const AvatarSection: React.FC<{
  models: ObjectModel[];
  goalModel: ObjectModel | null;
  goalBodyFat?: number;
  visibleMeasurements: KeyLabel[];
  deltas?: Map<string, MeasurementDelta>;
}> = ({ models, goalModel, goalBodyFat, visibleMeasurements, deltas }) => {
  // Null until the coach scrubs: the latest scan. Seeded from models.length
  // instead, a page opened straight on ?show=avatar started at -1 — the models
  // had not arrived — and stayed on "No avatar" after they had.
  const [picked, setIndex] = useState<number | null>(null);
  const index = picked ?? models.length - 1;
  const [highlighted, setHighlighted] = useState('');
  const [exportError, setExportError] = useState(false);
  const canExport = useFeature('avatarExport');

  const active = models[Math.min(index, models.length - 1)];
  const dates = useMemo(
    () => models.map((m) => new Date(m.createdAt)),
    [models],
  );

  if (!active) {
    return <p className="text-sm text-muted-foreground">No avatar for this client yet.</p>;
  }

  const isLatest = index === models.length - 1;

  return (
    <div className="flex flex-col gap-3.5">
      {models.length > 1 && (
        <ScanScrubber
          dates={dates}
          index={index}
          onChange={(i) => setIndex(i)}
        />
      )}

      <div className={`grid gap-3.5 ${goalModel ? 'lg:grid-cols-2' : ''}`}>
        <div className="flex flex-col gap-2">
          <div className="flex items-baseline justify-between gap-2">
            <Eyebrow tone={isLatest ? 'primary' : 'muted'}>
              {isLatest ? 'Now · measured' : 'Earlier scan · measured'}
            </Eyebrow>
            <span className="tabular font-mono text-[8.5px] uppercase tracking-[0.08em] text-muted-foreground">
              {dates[index]?.toLocaleDateString('en-GB', {
                day: 'numeric',
                month: 'short',
                year: 'numeric',
              })}
            </span>
          </div>
          {/* Portrait on a phone, where only the front view shows: the body is
              fitted to the frame's height, and a 320px frame the width of the
              screen drew it small enough to need zooming. The frame is wide
              enough for the arms at a closer zoom than the pairs use. */}
          <div className="flex h-[460px] gap-2 md:h-[320px]">
            <TwinView
              model={active}
              showMeasurement={highlighted}
              orbit
              zoom={1.6}
              className="flex-[1.6]"
            />
            {/* Hidden on phone: four WebGL contexts at 390px is the point at
                which the tab stops scrolling smoothly. */}
            <TwinView
              model={active}
              showMeasurement={highlighted}
              side
              orbit
              className="hidden flex-1 md:block"
            />
          </div>
          {canExport && (
            <div className="flex flex-wrap items-center gap-2">
              <button
                type="button"
                data-testid="download-avatar"
                onClick={() => {
                  try {
                    downloadAvatar(active.obj, active.measurements.height, active.createdAt);
                    setExportError(false);
                  } catch (e) {
                    console.error('Avatar export failed', e);
                    setExportError(true);
                  }
                }}
                className="flex min-h-11 items-center rounded-lg border border-border bg-card px-3 text-[11px] font-medium text-foreground transition-colors hover:bg-shell"
              >
                Download avatar (OBJ)
              </button>
              <span className="text-[11px] text-muted-foreground">
                {exportError
                  ? 'This scan has no avatar to download.'
                  : 'For CLO3D and other 3D tools. Centimetres, Y up.'}
              </span>
            </div>
          )}
        </div>

        {goalModel && (
          <div className="flex flex-col gap-2">
            <div className="flex items-baseline justify-between gap-2">
              <Eyebrow tone="projected">
                Goal · {Math.round(goalBodyFat ?? 0)} %
              </Eyebrow>
              <span className="projected-label rounded px-1.5 py-1 font-mono text-[8.5px] font-semibold uppercase tracking-wider">
                Simulated
              </span>
            </div>
            <div className="flex h-[460px] gap-2 md:h-[320px]">
              <TwinView
                model={goalModel}
                projected
                generated
                orbit
                zoom={1.6}
                className="flex-[1.6]"
              />
              <TwinView
                model={goalModel}
                projected
                generated
                side
                orbit
                className="hidden flex-1 md:block"
              />
            </div>
          </div>
        )}
      </div>

      <CoachCard
        testId="tape-readings"
        title={`Tape readings · ${isLatest ? 'latest scan' : 'this scan'}`}
        provenance="Tap a measurement to draw it on the body, and drag a body to turn it. Deltas are against the first scan."
      >
        <div className="grid gap-x-6 sm:grid-cols-2">
          {visibleMeasurements.map((m) => {
            const key = m.key as string;
            const value = valueOf(active.measurements, key);
            const delta = deltas?.get(key);
            const on = highlighted === key;
            return (
              <button
                key={key}
                type="button"
                onClick={() => setHighlighted(on ? '' : key)}
                aria-pressed={on}
                className={`-mx-2 flex items-baseline gap-2 rounded-md border-b border-divider px-2 py-2.5 text-left last:border-b-0 ${
                  on ? 'bg-accent' : 'hover:bg-shell'
                }`}
              >
                <span className="text-xs text-foreground-soft">{m.label}</span>
                <span className="tabular text-xs font-semibold">
                  {value > 0 ? `${value} ${unitFor(key)}` : '—'}
                </span>
                {/* Suppressed when the change carries no progress reading: a
                    number beside a measurement reads as progress whatever
                    colour it wears. */}
                {delta?.meaningful && (
                  <span
                    className={`tabular ml-auto text-xs font-semibold ${
                      // A zero holds its place — "measured, unchanged" is worth
                      // saying — but it is not progress, so it does not wear
                      // the colour progress wears.
                      delta.value === 0 ? 'text-muted-foreground' : 'text-primary'
                    }`}
                  >
                    {delta.text}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </CoachCard>
    </div>
  );
};
